
import { GoogleGenAI, Type } from "@google/genai";
import { Product, BlogPost, GenerationSettings, ChatMessage } from "../types";

/**
 * Generates a high-resolution, pure editorial image without any text or overlays.
 */
const generateBlogImage = async (ai: any, settings: GenerationSettings, title: string): Promise<string | undefined> => {
  try {
    const isRecipe = settings.category === 'Recipe';
    const isSocial = settings.category === 'Social';
    
    let aesthetic = "Beautiful home setting, sparkling surfaces, organised space.";
    if (isRecipe) {
      aesthetic = "Beautifully plated food, rustic kitchen setting, natural light, steam rising.";
    } else if (isSocial) {
      aesthetic = "Trendy, high-vibrancy lifestyle shot, close-up details, bright and punchy colours, modern home aesthetic.";
    }

    const prompt = `High-end editorial lifestyle photography for a blog titled "${title}". 
    Subject: ${settings.topic}. 
    Aesthetic: ${aesthetic} Soft pastel colour palette (pink, white, cream). 
    STRICT ZERO-TEXT POLICY: The image MUST be 100% clean. NO text, NO logos, NO overlaid writing, NO hashtags, and NO digital watermarks. 
    NO PEOPLE POLICY: The image MUST NOT include any people, faces, hands, or human figures. Focus entirely on the setting and objects.
    Composition: Cinematic wide shot, professional soft lighting.`;

    // Per guidelines: Generate images using gemini-2.5-flash-image by default.
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [{ text: prompt }]
      },
      config: {
        imageConfig: {
          aspectRatio: "16:9",
          imageSize: "1K"
        }
      }
    });

    for (const part of response.candidates[0].content.parts) {
      if (part.inlineData) {
        return `data:image/png;base64,${part.inlineData.data}`;
      }
    }
  } catch (error) {
    console.error("Editorial Image Generation Error:", error);
  }
  return undefined;
};

/**
 * Fetches trending SEO topics based on category
 */
export const getTrendingTopics = async (category: string): Promise<string[]> => {
  const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
  const prompt = `Search for the latest trending ${category} topics, viral home hacks, and seasonal cleaning trends in the UK for ${new Date().toLocaleString('en-GB', { month: 'long' })} ${new Date().getFullYear()}. 
  Based on real trending articles and social media buzz, provide 5 high-SEO topic titles (max 7 words each). 
  Return ONLY a JSON array of strings.`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: { type: Type.STRING }
        }
      }
    });
    return JSON.parse(response.text || "[]");
  } catch (error) {
    console.error("Trending Topics Error:", error);
    return [
      "Top 5 Spring Cleaning Secrets",
      "How to Clean Your Oven Naturally",
      "Organising a Small Kitchen on a Budget",
      "Removing Stubborn Carpet Stains",
      "5-Minute Morning Reset Routine"
    ];
  }
};

/**
 * Generates personalized cleaning advice as Lynsey, the Queen of Clean.
 */
export const getCleaningAdvice = async (
  message: string,
  history: ChatMessage[],
  products: Product[]
): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
  
  const productContext = products.map(p => 
    `- ${p.name}: ${p.description}. Shop: ${p.url}`
  ).join('\n');

  const systemInstruction = `
    You are Lynsey, the Queen of Clean (https://www.queenofclean.blog/). 
    You are a professional cleaning expert from the UK.
    Tone: Friendly, helpful, encouraging, and quintessentially British. Use terms like 'darling', 'love', and 'sparkle'.
    
    Rules:
    1. Only give advice related to cleaning, home organization, laundry, or home hacks.
    2. Recommend products from the list below when relevant. Always use UK English.
    3. Keep answers concise but warm.
    4. STRICTLY NO MARKDOWN formatting like asterisks (**) or hashes (#). Use plain text.
    
    PRODUCT LIST:
    ${productContext}
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: [
        ...history.map(m => ({
          role: m.role === 'assistant' ? 'model' : 'user',
          parts: [{ text: m.content }]
        })),
        { role: 'user', parts: [{ text: message }] }
      ],
      config: {
        systemInstruction,
        temperature: 0.7,
      },
    });

    return response.text || "Oh dear, I missed that bit, darling. Could you say it again?";
  } catch (error) {
    console.error("Cleaning Advice Error:", error);
    return "I'm having a little trouble with my connection, love. Let's try again!";
  }
};

export const generateBlogContent = async (
  settings: GenerationSettings,
  products: Product[]
): Promise<BlogPost> => {
  const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
  
  const prioritizedProducts = products.filter(p => settings.selectedProductIds.includes(p.id));
  const otherProducts = products.filter(p => !settings.selectedProductIds.includes(p.id));
  
  const productContext = [...prioritizedProducts, ...otherProducts].map(p => 
    `- ${p.name}: ${p.description}. Product Link: ${p.url}`
  ).join('\n');

  const detailInstructions = {
    Short: "Focus on a single 'Hero Tip' and 3 quick steps. Under 200 words.",
    Medium: "A standard editorial guide with intro, 5 detailed steps, and a conclusion. Approx 500 words.",
    Long: "A comprehensive Masterclass. Include the 'Science of Clean'. 800+ words.",
    'Top Tips': "A rapid-fire list of 10 essential tips or hacks. Use a bulleted list format."
  };

  const angleInstruction = {
    'Eco-Friendly': "Focus on natural ingredients and sustainable habits.",
    'Budget': "Highlight multi-use items and value for money.",
    'Speed Clean': "Focus on high-power tools and quick hacks that save time.",
    'Deep Dive': "Focus on thoroughness and professional-grade results."
  }[settings.angle];

  const prompt = `
    Write a professional ${settings.category} blog post and social media kit (including Facebook, Instagram, and TikTok posts) for https://www.queenofclean.blog/ about: "${settings.topic}".
    Target length: ${settings.length} (${detailInstructions[settings.length]}). Angle: ${settings.angle} (${angleInstruction}). Tone: ${settings.tone}.
    
    You are Lynsey, the Queen of Clean.
    
    PRIORITIZE recommending these specific products: ${prioritizedProducts.map(p => p.name).join(', ')}.
    
    UK English ONLY. Use terms like 'darling' and 'love'. 
    
    CLEAN TEXT RULE: No asterisks (**), no underscores (_), no # symbols.
    AFFILIATE LINKING: When you mention a product, use [Product Name](Product URL). 
    
    PRODUCT LIST:
    ${productContext}
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            summary: { type: Type.STRING },
            content: { type: Type.STRING },
            topTips: { type: Type.ARRAY, items: { type: Type.STRING } },
            socialPosts: {
              type: Type.OBJECT,
              properties: {
                instagram: { type: Type.STRING },
                facebook: { type: Type.STRING },
                tiktok: { type: Type.STRING }
              }
            },
            seoKeywords: { type: Type.ARRAY, items: { type: Type.STRING } },
            suggestedProductIds: { type: Type.ARRAY, items: { type: Type.STRING } }
          },
          required: ["title", "summary", "content", "topTips", "socialPosts", "seoKeywords", "suggestedProductIds"]
        },
        temperature: 0.8,
      }
    });

    const text = response.text || "{}";
    const data = JSON.parse(text);
    const featuredImage = await generateBlogImage(ai, settings, data.title);
    const suggestedProducts = products.filter(p => data.suggestedProductIds?.includes(p.id) || settings.selectedProductIds.includes(p.id));

    return {
      title: data.title || "Untitled Masterpiece",
      content: data.content || "Content generation failed.",
      summary: data.summary || "",
      featuredImage,
      topTips: data.topTips || [],
      socialPosts: data.socialPosts,
      seoKeywords: data.seoKeywords || [],
      suggestedProducts: suggestedProducts
    };
  } catch (error) {
    console.error("Blog Generation Error:", error);
    throw error;
  }
};
